import { ParsedUrlQuery } from 'querystring';

import { useEffect, useState, useRef, useMemo } from 'react';

import { useParams, useRouter, useSearchParams } from 'next/navigation';

import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import {
  hotelItemsTOStringify,
  validateGuestsChildrenAges,
  generateSearchRooms,
} from '@components/views/landing/components/booker/common/bookerUtils';
import clean from '@utils/cleanObject';
import { useDebounceValue, useOutsideClick } from '@hooks/hooks';
import SearchGuest from '@components/ui/search/SearchGuest/SearchGuest';
import { WebUrlType } from '@api/v3';
import SearchBarDateRangeInput from '@components/ui/search/SearchBarInput/SearchBarDateInput/SearchBarRangeDatePickerInput/SearchBarDateRangePickerInput';
import {
  formatMomentDate,
  getCurrentDate,
  getNMonthsLater,
  HOTEL_SEARCH_DATE_FORMAT,
  isInclusivelyAfterDay,
} from '@utils/dateUtil';
import { SearchButton } from '@components/views/components/listing/ListingStyle';
import { HOTEL_PAST_SEARCH } from '@constants/constants';
import { handleFilterAndUpdateUrl } from '@utils/utils';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import { useSearchURL } from '@api/data-loaders/resource';
import { paramsToObject } from '@components/views/utils';
import useRouterNext from '@hooks/useRouterNext';
import useReplaceNext from '@hooks/useReplaceNext';
import { useCalendarHoliday } from '@components/DatePicker/DateRangePicker/getCalendarHolidays';

import SearchLocation from './components/SearchLocation/DesktopSearchLocation';
import {
  SearchBarItemWrapper,
  SearchButtonWrapper,
  StyledListingSearch,
} from './SearchBarStyle';
import { SearchBarProps } from './SearchBarInterface';
import { convertQueryRoomToHotelRooms, formatRooms } from '../../utils';
import { useLocationSearch, useUrlSearch } from './hooks';

const SearchBar = ({
  location,
  setDates,
  setIsFetchedItems,
  setIsLocationEntered,
  setLocation,
  isLocationEntered,
  startDate,
  endDate,
  locationInputVal,
  setLocationInputVal,
  initialHotelRooms: hotelRooms,
  setInitialHotelRooms: setHotelRooms,
  onResearchClick,
  isHotelListing,
  setSearchLoading,
}: SearchBarProps) => {
  const [buttonDisable, setButtonDisable] = useState<boolean>(false);
  const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);

  const { push } = useRouterNext();
  const { replace } = useReplaceNext();
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = useParams();
  const slug = Array.isArray(params?.slug) ? params?.slug[0] : params?.slug;

  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  // const isFetchingHotels = useIsFetching(['hotelListing']);
  // const isFetchingHotelDetail = useIsFetching(['hotelDetail']);
  const wrapperRef = useRef<any>(null);
  const { setHotelDateSelection, isHotelDateSelected, setActiveLocation } =
    useSearchBarContext();

  const urlData = useSearchURL();
  const debounceLocation = useDebounceValue<string>(locationInputVal);
  const { data: urlSearchData } = useUrlSearch(slug, urlData);

  useEffect(() => {
    if (!urlSearchData?.text) return;

    setActiveLocation(urlSearchData);
    setLocation(urlSearchData);
  }, [urlSearchData, setActiveLocation, setLocation]);

  const {
    data: locationsList,
    isError: searchLocationError,
    isLoading: searchLocationLoading,
  } = useLocationSearch(debounceLocation);

  useEffect(() => {
    const currentLocation = urlSearchData;
    if (currentLocation && currentLocation.text) {
      setActiveLocation(currentLocation);
      setLocation(currentLocation);
    }
  }, [query, urlSearchData, setActiveLocation, setLocation]);

  // ============================================================
  // -Custom functions to control the rooms
  // -using this function we can remove a room
  // =============================================================

  const handleSubmitSearch = async (): Promise<void> => {
    setSearchLoading?.(true);
    if (!location.url) {
      setIsLocationEntered(false);
      return;
    }

    const rooms = generateSearchRooms(hotelRooms, true);

    const newQuery = clean({
      in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
      out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
      room: rooms,
      slug: params?.slug as string,
      sort: query.sort as string,
      size: query.size as string,
    });

    // ===================================
    // Add new search to the local storage
    // ===================================
    const items = localStorage.getItem(HOTEL_PAST_SEARCH)
      ? JSON.parse(localStorage.getItem(HOTEL_PAST_SEARCH) || '')
      : [];

    const newSearch = clean({
      in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
      out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
      rooms: hotelRooms,
      slug: location?.url,
      ...location,
    });

    if (!items?.find((i: any) => i.url === location.url)) {
      items.unshift(newSearch);
    } else {
      const target = items?.find((i: any) => i.url === location.url);

      const targetIndex = items.indexOf(target);
      items.splice(targetIndex, 1);
      items.unshift(newSearch);
    }

    localStorage.setItem(
      HOTEL_PAST_SEARCH,
      JSON.stringify(items, hotelItemsTOStringify)
    );
    onResearchClick?.();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { slug, ...nextQuery } = newQuery;

    if (
      location.type === WebUrlType.HOTEL_CITY ||
      location.type === WebUrlType.HOTEL_REGION ||
      location.type === WebUrlType.HOTEL_CHAIN ||
      location.type === WebUrlType.HOTEL_THEME
    ) {
      if (location.redirectUrl) {
        const roomUrlStr = generateSearchRooms(hotelRooms, false);
        const href = handleFilterAndUpdateUrl(
          clean({
            in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT) || null,
            out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT) || null,
            slug: location?.url,
            room: roomUrlStr,
          })
        );
        router.push(`/${href}`);
      } else {
        setIsFetchedItems(true);
        const currentPath = params?.slug as string;
        if (currentPath !== location.url) {
          const roomUrlStr = generateSearchRooms(hotelRooms, false);
          const href = handleFilterAndUpdateUrl(
            clean({
              in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT) || null,
              out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT) || null,
              slug: location?.url,
              room: roomUrlStr,
            })
          );
          router.push(`/${href}`);
        } else {
          push({
            pathname: `/${location?.url}`,
            query: nextQuery,
          });
        }
      }
    } else if (location.type === WebUrlType.HOTEL) {
      const roomUrlStr = generateSearchRooms(hotelRooms, true);
      setButtonDisable(false);
      push({
        pathname: `/${location?.url}`,
        query: {
          in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
          out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
          room: roomUrlStr,
        },
      });
    } else {
      setButtonDisable?.(false);
      replace({
        pathname: '/',
        query: nextQuery,
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setLocationInputVal(e.target.value);
    setButtonDisable?.(true);
  };

  const handleLocationClicked = (i: any): void => {
    setLocation(i);
    setLocationInputVal(i.text);
    setButtonDisable?.(true);
  };

  const onInputBlur = (): void => {
    if (!debounceLocation || debounceLocation.length < 3) {
      setLocation({ id: 0, text: '' });
    }
  };

  // ====================================
  // Toggle the result filter base on the active selected box
  // ====================================
  useOutsideClick(wrapperRef, () => {
    setIsLocationEntered(true);
  });
  useEffect(() => {
    if (urlSearchData) {
      setLocationInputVal(urlSearchData?.text || '');
    }
  }, [urlSearchData]);
  useEffect(() => {
    if (!isHotelDateSelected) return;
    setDatePickerVisible(Boolean(isHotelDateSelected));
  }, [isHotelDateSelected]);

  // ================================
  // rendering content start here
  // =================================
  useEffect(() => {
    if (startDate && endDate) {
      if (
        formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT) != query.in ||
        formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT) != query.out
      ) {
        setButtonDisable?.(true);
      } else {
        setButtonDisable?.(false);
      }
    } else {
      setButtonDisable?.(false);
    }
  }, [startDate, endDate, setButtonDisable]);

  useSkipEffectOnMount(() => {
    const queryRooms = convertQueryRoomToHotelRooms(query.room as string);
    const isChildError = validateGuestsChildrenAges(hotelRooms);
    const formatedQueryRooms = formatRooms(queryRooms);
    const formatedHotelRooms = formatRooms(hotelRooms);
    if (
      isChildError ||
      JSON.stringify(formatedQueryRooms) === JSON.stringify(formatedHotelRooms)
    )
      return setButtonDisable(false);
    setButtonDisable(true);
  }, [hotelRooms]);
  const { data: initialData } = useCalendarHoliday();

  return (
    <StyledListingSearch>
      <SearchBarItemWrapper overFlow='visible' ref={wrapperRef}>
        <SearchLocation
          label='Otel / Konum'
          text={locationInputVal}
          setText={setLocationInputVal}
          handleChange={handleChange}
          data={locationsList}
          handleLocationClicked={handleLocationClicked}
          onInputBlur={onInputBlur}
          isLocationEntered={isLocationEntered}
          searchTextError={searchLocationError}
          searchTextLoading={searchLocationLoading}
        />
      </SearchBarItemWrapper>

      <SearchBarItemWrapper>
        <SearchBarDateRangeInput
          initialData={initialData}
          label='Giriş - Çıkış Tarihleri'
          popoverProps={{
            onVisibleChange: (visible) => {
              setDatePickerVisible(visible);
              setHotelDateSelection(visible);
            },
            isOpen: datePickerVisible,
          }}
          placeholder='Giriş - Çıkış Tarihleri'
          dateProps={{
            startDate,
            endDate,
            onDatesChange: setDates,
            isOutsideRange: (day) =>
              !isInclusivelyAfterDay(day, getCurrentDate()) ||
              (startDate
                ? !endDate
                  ? isInclusivelyAfterDay(day, getNMonthsLater(startDate, 1))
                  : false
                : false),
          }}
        />
      </SearchBarItemWrapper>
      <SearchBarItemWrapper>
        <SearchGuest
          rooms={hotelRooms}
          setHotelRooms={setHotelRooms}
          isHotelListing={isHotelListing}
          searchText='Kişi, Oda Sayısı'
        />
      </SearchBarItemWrapper>
      <SearchButtonWrapper ref={wrapperRef}>
        <SearchButton
          data-testid='listing-search-button'
          onClick={handleSubmitSearch}
          variant='Secondary'
          style={{ pointerEvents: datePickerVisible ? 'none' : 'auto' }}
          disabled={!buttonDisable}
        >
          Yeniden Ara
        </SearchButton>
      </SearchButtonWrapper>
    </StyledListingSearch>
  );
};

export default SearchBar;
